import { useMultistepForm } from '@/hooks/use-multistep-form.tsx';
import { STEPS, getRegisterSchema } from './register/steps.tsx';
import { Form } from '@/components/ui/form.tsx';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { Button } from '@/components/ui/button.tsx';
import { useRegisterUserQuery } from '@/features/auth/hooks/mutations/register.ts';
import { useConfirmEmailQuery } from '@/features/auth/hooks/mutations/confirm-email.ts';
import { Link, useNavigate } from 'react-router';
import { LoadingSpinner } from '@/components/ui/loading-spinner.tsx';
import { StatusAlert } from '@/features/auth/components/status-alert.tsx';
import Logo from '@tenant/logo';

export const Register = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const registerSchema = getRegisterSchema({
    firstNameRequired: t('auth.errors.firstNameRequired'),
    firstNameLength: t('auth.errors.firstNameLength'),
    lastNameRequired: t('auth.errors.lastNameRequired'),
    lastNameLength: t('auth.errors.lastNameLength'),
    emailRequired: t('auth.errors.emailRequired'),
    emailValid: t('auth.errors.emailValid'),
    passwordRequired: t('auth.errors.passwordRequired'),
    passwordComplexity: t('auth.errors.passwordComplexity'),
    codeRequired: t('auth.errors.codeRequired'),
    codeLength: t('auth.errors.codeLength'),
    codeNumber: t('auth.errors.codeNumber'),
    codePositive: t('auth.errors.codePositive'),
  });

  const {
    step: StepElement,
    isFirstStep,
    isLastStep,
    currentStepIndex,
    next,
  } = useMultistepForm(STEPS.map((S) => <S.Element />));

  const form = useForm({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      basicDetails: {
        firstName: '',
        lastName: '',
        email: '',
        password: '',
      },
      confirmEmail: {
        email: '',
        code: '',
      },
    },
  });

  const registerMutation = useRegisterUserQuery({
    onSuccess: (data) => {
      if (data?.body?.email) {
        form.setValue('confirmEmail.email', data.body.email);
      } else {
        const email = form.getValues('basicDetails.email');
        form.setValue('confirmEmail.email', email);
      }
      next();
    },
  });

  const confirmEmailMutation = useConfirmEmailQuery({
    onSuccess: () => {
      setTimeout(() => {
        navigate('/');
      }, 700);
    },
  });

  async function onSubmit(values: z.infer<typeof registerSchema>) {
    const isValid = await form.trigger(STEPS[currentStepIndex]?.schema);

    if (isLastStep && isValid) {
      confirmEmailMutation.mutate({
        code: values.confirmEmail.code,
        email: values.basicDetails.email,
      });
    }
  }

  const onNext = async () => {
    const isValid = await form.trigger(STEPS[currentStepIndex]?.schema);

    if (isFirstStep && isValid) {
      registerMutation.mutate({
        firstName: form.getValues('basicDetails.firstName'),
        lastName: form.getValues('basicDetails.lastName'),
        email: form.getValues('basicDetails.email'),
        password: form.getValues('basicDetails.password'),
      });
    }
  };

  const errorMessage = registerMutation.error?.message || confirmEmailMutation.error?.message;

  return (
    <div>
      <div className="flex flex-col items-center justify-center gap-2">
        <Logo width="200" height="58" />
      </div>
      <div className="mt-8 flex max-w-[400px] flex-col gap-4">
        {!!errorMessage && <StatusAlert type="error" message={errorMessage} />}
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            {StepElement}
            {isFirstStep && (
              <Button
                disabled={registerMutation.isPending}
                type="button"
                className="flex w-full items-center gap-2"
                size="lg"
                onClick={onNext}
              >
                {registerMutation.isPending && <LoadingSpinner size={24} />}
                <span className="text-[16px]">{t('auth.register.signupButton')}</span>
              </Button>
            )}
            {isLastStep && (
              <Button
                disabled={confirmEmailMutation.isPending}
                type="submit"
                className="flex w-full items-center gap-2"
                size="lg"
              >
                {confirmEmailMutation.isPending && <LoadingSpinner size={24} />}
                <span className="text-[16px]">{t('auth.confirmEmail.confirmButton')}</span>
              </Button>
            )}
          </form>
        </Form>
        {isFirstStep && (
          <div className="flex justify-center gap-1 text-center">
            <span>{t('auth.register.alreadyHaveAccount')}</span>
            <Link to="/" className="link-underline">
              {t('auth.register.loginLink')}
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};
